import React, { useEffect, useRef } from "react";
import { Container, Row, Col, Accordion } from "react-bootstrap";
import Icon from "@mdi/react";
import { mdiArrowRightThin } from "@mdi/js";
import { motion } from "framer-motion";

// Images
import AboutUsImg1 from "../../images/about-img-1.jpg";
import AboutUsImg2 from "../../images/about-img-2.jpg";
import CircleEffect from "../../images/circle.png";

// Animation config
const imageAnimation = {
  whileInView: { scale: 1 },
  initial: { scale: 0.7 },
  transition: {
    type: "linear",
    stiffness: 100,
    duration: 1,
  },
};

export default function AboutUs() {
  const titleRef = useRef(null);

  useEffect(() => {
    const element = titleRef.current;
  
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("animate-lines");
        } else {
          entry.target.classList.remove("animate-lines");
        }
      });
    }, { threshold: 0.3 });
  
    if (element) observer.observe(element);
  
    return () => {
      if (element) observer.unobserve(element);
    };
  }, []);
  

  return (
    <>
      {/* Section 1 */}
      <section className="section about-us-section" id="about">
        <Container>
          <Row>
            <Col md={12}>
              <div className="section-title-box">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <h1 className="section-title" ref={titleRef}>
                    <span className="text-primary">About</span> Us
                    <div className="title-line before-line" />
                    <div className="title-line after-line" />
                  </h1>
                </motion.div>
              </div>
            </Col>
          </Row>

          <Row className="align-items-center">
            <Col lg={5}>
              <motion.div
                className="about-us-img d-inline-block mb-lg-0 mb-5 hover-effect"
                {...imageAnimation}
              >
                <img src={AboutUsImg1} alt="Team collaborating" className="img-fluid about-img" />
                <img src={CircleEffect} alt="" className="circle-img" />
              </motion.div>
            </Col>
            <Col lg={{ span: 6, offset: 1 }}>
              <motion.div {...imageAnimation}>
                <h1 className="mb-4">
                  We Are The Best And Trusted{" "}
                  <span className="text-primary">Real Estate</span> Agent
                </h1>
                <p className="text-white opacity-85 fs-16">Our commitment to excellence drives us to deliver the most competitive real estate deals on the market...</p>
                <p className="text-white opacity-85 fs-16 mb-4 pb-3">With a deep understanding of the market and a passion for customer satisfaction, we ensure every transaction is seamless...</p>

                <Row>
                  <Col xl={10}>
                    <Accordion defaultActiveKey="0">
                      {[
                        {
                          question: "What types of real estate deals do you offer?",
                          answer: "We offer a variety of deals including residential homes, commercial properties...",
                        },
                        {
                          question: "How do I know if a property is a good deal?",
                          answer: "Our experienced team evaluates each property based on market trends...",
                        },
                        {
                          question: "Can you help with financing options?",
                          answer: "Yes, we work with trusted financial partners to help you explore the best solutions...",
                        },
                      ].map((item, idx) => (
                        <Accordion.Item eventKey={idx.toString()} key={idx}>
                          <Accordion.Header>
                            <i className="bi bi-question-lg me-2 text-primary fs-26"></i>{" "} {item.question}
                          </Accordion.Header>
                          <Accordion.Body>{item.answer}</Accordion.Body>
                        </Accordion.Item>
                      ))}
                    </Accordion>
                  </Col>
                </Row>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Section 2 */}
      <section className="section about-us-section">
        <Container>
          <Row className="align-items-center pb-xl-4">
            <Col lg={{ span: 5, offset: 1 }} className="order-lg-2">
              <motion.div
                className="about-us-img d-inline-block mb-lg-0 mb-5 hover-effect" {...imageAnimation}
              >
                <img src={AboutUsImg2} alt="Agent showcasing property" className="img-fluid about-img" />
                <img src={CircleEffect} alt="" className="circle-img" />
              </motion.div>
            </Col>
            <Col lg={6} className="order-lg-1">
              <motion.div {...imageAnimation}>
                <h1 className="mb-4 me-xl-5">
                  We Are Offering The Best{" "}
                  <span className="text-primary">Real Estate</span> Deals
                </h1>
                <p className="text-white text-opacity-85 fs-16 mb-4">At Trivik Builders, we understand that finding the perfect property...</p>

                <ul className="list-unstyled text-white opacity-85 fs-15 mb-4">
                  {[
                    "Access to exclusive deals and competitive pricing in prime locations.",
                    "Dedicated to helping you find the perfect property to match your goals.",
                    "Unmatched market insights and industry expertise.",
                  ].map((text, index) => (
                    <motion.li
                      key={index}
                      className="d-flex align-items-center mb-2"
                      whileHover={{
                        x: 10,
                        scale: 1.02,
                        transition: { type: "spring", stiffness: 300 },
                      }}
                      transition={{ duration: 0.3 }}
                    >
                      <Icon
                        path={mdiArrowRightThin}
                        className="icon-size-24 text-primary me-2"
                      />
                      {text}
                    </motion.li>
                  ))}
                </ul>

                <Row>
                  {[
                    [
                      { icon: "bi-currency-dollar", text: "Affordable Prices" },
                      { icon: "bi-geo-alt", text: "Prime Locations" },
                      { icon: "bi-graph-up-arrow", text: "High Investment Returns"},
                    ],
                    [
                      { icon: "bi-briefcase", text: "Expert Guidance" },
                      { icon: "bi-currency-dollar", text: "Secure Transactions" },
                      { icon: "bi-shield-lock", text: "Diverse Property Options" },
                    ],
                  ].map((col, colIndex) => (
                    <Col sm={6} key={colIndex}>
                      {col.map((item, i) => (
                        <motion.div key={i}
                          whileHover={{
                            scale: 1.03,
                            boxShadow: "0 8px 24px rgba(0, 123, 255, 0.25)",
                          }}
                          transition={{ type: "spring", stiffness: 300 }}
                          className="card-hover-animated d-flex align-items-center cursor-pointer p-2 border border-soft-secondary shadow-sm rounded mb-3"
                        >
                          <div className="content d-flex align-items-center w-100">
                            <div className="flex-shrink-0">
                              <div className="avatar avatar-sm bg-soft-primary border border-soft-primary rounded text-primary">
                                <i className={`bi ${item.icon}`}></i>
                              </div>
                            </div>
                            <div className="flex-grow-1 ms-3 fs-18">{item.text}</div>
                          </div>
                        </motion.div>
                      ))}
                    </Col>
                  ))}
                </Row>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
}
